import { UserService } from "@tytapp/user";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";

export interface NotificationAction {
    label?: string;
    url?: string;
    icon?: string;
}

export interface NotificationItem {
    id?: number;
    category?: 'info' | 'warning' | 'error';
    text: string;
    url?: string;
    open_in_new_tab?: boolean;
    description?: string;
    timestamp?: Date;
    icon?: string;
    extra_actions?: NotificationAction[];
    onDismiss?: () => void;
    style?: 'normal' | 'attention' | 'muted';
    image?: string;
    demoOnly?: boolean;
}

export class NotificationsService {

    private _list: NotificationItem[] = [];
    private _changed = new BehaviorSubject<NotificationItem[]>([]);
    private _countChanged = this._changed.pipe(map(v => v.length));
    private _visibleChange = new Subject<boolean>();

    public unseenChanged = new BehaviorSubject<number>(this.unseen);

    private _visible = false;
    private _unseen = 0;

    private _totalItems = 0;
    private _totalItemsChanged = new BehaviorSubject<number>(this.totalItems);


    get visible() {
        return this._visible;
    }

    set visible(value) {
        this._visible = value;
        this._visibleChange.next(value);
    }

    get visibleChange() {
        return this._visibleChange;
    }

    get list() {
        return this._list.slice();
    }

    get changed() {
        return <Observable<NotificationItem[]>>this._changed;
    }

    get countChanged() {
        return this._countChanged;
    }

    get count() {
        return this._list.length;
    }

    get unseen(): number {
        return this._unseen;
    }

    set unseen(value: number) {
        this._unseen = value;
        this.unseenChanged.next(value);
    }

    get totalItems() {
        return this._totalItems;
    }

    set totalItems(value: number) {
        this._totalItems = value;
        this._totalItemsChanged.next(value);
    }

    get hasMoreItems() {
        return this.list.length < this.totalItems;
    }

    get totalItemsChanged() {
        return this._totalItemsChanged.asObservable();
    }

    markSeen(): void {
        this.unseen = 0;
    }

    showList() {
        this.visible = true;
    }

    closeList() {
        this.visible = false;
    }

    removeById(id: number) {
        this._list = this._list.filter(x => x.id !== id);
        this._changed.next(this._list);

        if (this._list.length === 0)
            this.closeList();
    }

    remove(notif: NotificationItem) {
        this._list = this._list.filter(x => x !== notif);
        this._changed.next(this._list);
        this.unseen = Math.max(0, this._unseen - 1);

        if (this._list.length === 0)
            this.closeList();
    }

    dismiss(notification: NotificationItem) {
        this.remove(notification);
        if (notification.onDismiss)
            notification.onDismiss();
    }

    add(notification: NotificationItem, incrementUnseen = true) {
        if (notification.id) {
            let existing = this._list.find(x => x.id === notification.id);
            if (existing) {
                return;
            }
        }

        this._list.unshift(notification);
        this._changed.next(this._list);
        if (!this.visible) {
            if (incrementUnseen) {
                this.unseen = this._unseen + 1;
            }
        }
    }
}